// What is actually in a room — assembled from the two endpoints that each know
// part of the answer.
//
// ⚠️ THE ROOM ENDPOINT IS NOT THE WHOLE ROOM. `GetDeviceDetailsbyUserIdAndRoomId`
// filters by device TYPE server-side. A curtain is installed in the room, mapped
// to the user, `IsActive = 1` — and simply never arrives. Nothing errors and no
// field is blank; the device is just absent, which is why this reads as
// "assigned but invisible" and gets diagnosed as a bad assignment.
//
// Confirmed against the live backend on 2026-08-01 (device 5586900, a Curtain
// Controller in room 38 of location 2, owner user 1):
//
//   GetDeviceDetailsbyUserIdAndRoomId   6 devices — no curtain
//   GetDeviceListsbyUserIDandLocationId 7 devices for the same room, curtain included
//
// It is one hardcoded list, in the backend's `select.models.js`:
//
//   room     ... and a.DeviceId not in (2,13,3,4,19,20,21,26,30,36,45,47,51)
//   location ... and a.DeviceId <> 3 and a.DeviceId <> 19 and a.DeviceId <> 21
//
// which is motion/door/safe/temperature/washroom sensors, RF receiver, Wi-Fi
// remote buttons, spike guard, BOTH curtains (13, 51), the door lock, the IR
// blaster and the single switch with external button. Two different blind spots,
// so the room's real list is the union. The location endpoint is the one worth
// merging in because it carries `RoomID` and the same rich row (Status, Outputs,
// ImageLocation), so a merged-in device renders like any other instead of as a
// half-blank card.
//
// What no client-side merge can reach: DeviceIDs 3, 19 and 21 are excluded by
// BOTH, so a door lock, IR blaster or external-button switch sitting in a room
// needs the backend list changed. The desktop shell approximates it from
// GetListofDevicesbyUserId by matching room + location NAME.
//
// Both are filtered by `UserDeviceMapping` server-side, so merging cannot show a
// user a device they have no access to. That is why this uses the location list
// and not `GetDevicesbyRoomId`, which answers for the room but for nobody in
// particular.
//
// The cache path does not need any of this: it is built from
// `GetDevicesInstalledForSinkingDatabyUserID`, the install table itself, which
// has no type filter. This is the REST path's problem alone.

export const unoOf = (d) => String((d && (d.UniqueNumber ?? d.uniqueNumber)) ?? '');

const roomIdOf = (d) => (d == null ? null : (d.RoomID ?? d.RoomId));

/**
 * A location-list row in the room list's shape.
 *
 * ⚠️ `Location` MEANS OPPOSITE THINGS IN THE TWO REPLIES. A room row aliases the
 * LOCATION name to it (`Location: "Home", RoomName: "Hall"`); a location row
 * aliases the ROOM name to it (`Location: "Hall"`). Copying the row through
 * unchanged would leave a device claiming its room as its location.
 */
function asRoomRow(d, roomName, locationName) {
  return {
    ...d,
    RoomID: roomIdOf(d),
    RoomName: d.RoomName ?? d.Location ?? roomName ?? null,
    Location: locationName ?? null,
    // The room reply spells the timestamp with a lower-case l and the liveness
    // checks read that spelling first (useHideOffline, DeviceStatusDot). Both
    // are set so the online dot works whichever one is consulted.
    lastUpdatedDateTime: d.lastUpdatedDateTime ?? d.LastUpdatedDateTime ?? null,
    // Only the room reply joins the favourites flag; a merged device is simply
    // not starred rather than undefined.
    favourite: d.favourite ?? 0,
  };
}

/**
 * Room rows plus any device the room endpoint left out.
 *
 * Room rows are kept first and untouched — they are the richer, authoritative
 * shape. Merged devices are appended; callers sort by `SortOrder` anyway.
 *
 * @param roomRows      GetDeviceDetailsbyUserIdAndRoomId rows
 * @param locationRows  GetDeviceListsbyUserIDandLocationId rows (all rooms)
 * @param roomId        the room being listed
 */
export function mergeRoomDevices(roomRows, locationRows, roomId) {
  const rows = Array.isArray(roomRows) ? roomRows : [];
  const candidates = Array.isArray(locationRows) ? locationRows : [];
  if (!candidates.length) return rows;

  const seen = new Set(rows.map(unoOf).filter(Boolean));
  // Taken from a row we already have, so a merged device inherits the names the
  // room endpoint would have given it. Null when the room came back empty —
  // which happens when EVERY device in it is one of the excluded types.
  const roomName = rows.length ? (rows[0].RoomName ?? null) : null;
  const locationName = rows.length ? (rows[0].Location ?? null) : null;

  const extra = [];
  for (const d of candidates) {
    const uno = unoOf(d);
    // The location reply unions the grouped devices back in, so the same device
    // can appear twice; `seen` grows as we go to keep it once.
    if (!uno || seen.has(uno)) continue;
    if (String(roomIdOf(d)) !== String(roomId)) continue;
    seen.add(uno);
    extra.push(asRoomRow(d, roomName, locationName));
  }

  return extra.length ? [...rows, ...extra] : rows;
}

export default mergeRoomDevices;
